import { SimpleGrid } from '@chakra-ui/layout';
import { useBreakpointValue } from '@chakra-ui/react';
import { AnimatePresence } from 'framer-motion';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ReactComponent as HighlightedIcon } from '../../../../assets/images/icons/highlighted-models.svg';
import { ReactComponent as PillIcon } from '../../../../assets/images/icons/ic-pill.svg';
import { ReactComponent as CloseIcon } from '../../../../assets/images/icons/mevo/ic-exit-simple.svg';
import { ReactComponent as LeftArrowIcon } from '../../../../assets/images/icons/mevo/left-arrow.svg';
import { ReactComponent as TextObservation } from '../../../../assets/images/icons/mevo/ic-text-obsevation.svg';
import { GrowMotionBox } from '../../../../components/Motion/GrowMotionBox';
import MedicineStripe from '../../../../components/search/MedicineStripe';
import { Box } from '../../../../components/UI-CHAKRA/Box';
import { Button } from '../../../../components/UI-CHAKRA/Button';
import { Divider } from '../../../../components/UI-CHAKRA/Divider';
import { Flex } from '../../../../components/UI-CHAKRA/Flex';
import { IconButton } from '../../../../components/UI-CHAKRA/IconButton';
import { Spinner } from '../../../../components/UI-CHAKRA/Spinner';
import { Text } from '../../../../components/UI-CHAKRA/Text';
import { capitalizeFirstLetter } from '../../../../helpers/string';
import { useLoading } from '../../../../helpers/useLoading';
import { useModelStore } from '../../../../stores/modelStore';
import { usePrescriptionStore } from '../../../../stores/prescriptionStore';
import { ModelDetails } from '../../services';
import { amplitudeModule } from '../../../../shared/amplitude';

interface HighlightedModelsProps {
  onClose(): void;
  onConfirm(): void;
  handleHighlightedModelEvents: (event: string, extraProps?: any) => void;
  handleSponsoredModelsConfig: (
    operation: 'reset' | 'update' | 'none',
    counterExists: boolean,
    currentCounter: number | null
  ) => Promise<Array<{ chave: string; valor: number | Date }>>;
  sponsoredCounter: number;
}

export default function HighlightedModels(props: HighlightedModelsProps) {
  const [loading, runWithLoading] = useLoading();
  const { onConfirmModelInsertion, sponsoredModelDetails, fetchSponsoredModelDetails } = useModelStore();

  const { setShowedHighlightedModels } = usePrescriptionStore();
  const prescriberConfiguration = usePrescriptionStore((store) => store.prescriberConfiguration);
  const [currentModelDetail, setCurrentModelDetail] = useState<ModelDetails | null>(null);

  const modalMaxWidth = useBreakpointValue({ 
    base: '90vw', 
    md: '500px', 
    lg: '540px' 
  }, { ssr: false });
  
  const modalMaxHeight = useBreakpointValue({ 
    base: '70vh', 
    md: '65vh', 
    lg: '70vh' 
  }, { ssr: false });

  const resetHours = parseInt(process.env.REACT_APP_SPONSORED_RESET_HOURS || '24', 10);
  const days = Math.max(1, Math.ceil(resetHours / 24));
  const message = `Lembrar em ${days} dia${days > 1 ? 's' : ''}`;

  useEffect(() => {
    setShowedHighlightedModels(true);

    runWithLoading(async () => {
      if (sponsoredModelDetails.length > 0) return;

      await fetchSponsoredModelDetails();
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onBackToModels = () => {
    setCurrentModelDetail(null);
  };

  const onDismiss = useCallback(() => {
    amplitudeModule.popUpClicked({
      pop_up_id: 1,
      pop_up_type: 'modelos patrocinados',
      pop_up_title: 'Modelos',
      pop_up_content: 'Facilite o preenchimento da receita com modelos.',
      pop_up_dismiss: true,
      pop_up_later: false,
      pop_up_agreed: false,
      partner_id: prescriberConfiguration?.idParceiro ?? 0,
      partner_name: prescriberConfiguration?.Nome ?? '',
    });
    props.onClose();
  }, [props, prescriberConfiguration]);

  const onAddMedicines = useCallback(async () => {
    if (!currentModelDetail) return;
    runWithLoading(async () => {
      const idsMedicamentos = currentModelDetail.Medicamento.map((medicine) => medicine.IdMedicamento);
      props.handleHighlightedModelEvents('backoffice_prescription_models_view_addmodel', {
        idMedicamento: idsMedicamentos,
        idPrescricaoModelo: currentModelDetail.id,
      });

      await onConfirmModelInsertion(currentModelDetail.id || 0, currentModelDetail, false);
      props.onConfirm();
    });
  }, [currentModelDetail, onConfirmModelInsertion, props, runWithLoading]);

  const handleModelDetails = useCallback(
    async (modelDetails: ModelDetails) => {
      if (!modelDetails.id) return;

      props.handleHighlightedModelEvents('backoffice_prescription_models_view_clicou', {
        idPrescricaoModelo: modelDetails.id,
      });

      amplitudeModule.popUpClicked({
        pop_up_id: 1,
        pop_up_type: 'modelos patrocinados',
        item: modelDetails.Medicamento?.map((m) => ({
          id: m.IdMedicamento,
          name: m.Nome ?? m.Label ?? '',
          description: m.Posologia ?? '',
          sponsor: m.Laboratorio ?? '',
        })),
        pop_up_title: modelDetails.Nome ?? '',
        pop_up_content: 'Facilite o preenchimento da receita com modelos.',
        pop_up_dismiss: false,
        pop_up_later: false,
        pop_up_agreed: true,
        partner_id: prescriberConfiguration?.idParceiro ?? 0,
        partner_name: prescriberConfiguration?.Nome ?? '',
      });

      setCurrentModelDetail(modelDetails);
    },
    [props, prescriberConfiguration]
  );

  const ModelDetailContent = useMemo(() => {
    if (!currentModelDetail) return null;

    return (
      <GrowMotionBox>
        <Box w={'100%'} p={'20px'}>
          <Flex flexDirection={'column'} gap={'20px'}>
            <Flex alignItems={'center'} justifyContent={'space-between'}>
              <Flex
                flexDirection={'row'}
                alignItems={'center'}
                gap={'20px'}
                _hover={{ cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => onBackToModels()}
              >
                <LeftArrowIcon />
                <Text color={'#3D0E6B'} fontWeight={700}>
                  Voltar para modelos
                </Text>
              </Flex>
              <IconButton size={'xs'} aria-label={'close-dialog-model'} variant={'ghost'} onClick={onDismiss}>
                <CloseIcon />
              </IconButton>
            </Flex>
            <Text fontSize={'24px'} fontWeight={700} lineHeight={'33px'} color={'#2f2f2f'} textTransform={'capitalize'}>
              {currentModelDetail.Nome}
            </Text>
            <Text fontSize={'16px'} fontWeight={700} lineHeight={'33px'} color={'#3D0E6B'} textTransform={'capitalize'}>
              Receita
            </Text>
            {currentModelDetail.Medicamento &&
              currentModelDetail.Medicamento.length > 0 &&
              currentModelDetail.Medicamento.map((medicine, index) => (
                <>
                  <Flex gap={'5px'} alignItems={'flex-start'}>
                    <MedicineStripe medicine={medicine} height="25px" />
                    <Flex flexDirection={'row'} alignItems={'flex-start'} gap={'10px'} w={'100%'}>
                      <Flex gap={'5px'} flexDir={'column'} w={'100%'}>
                        <Text fontSize={'14px'} lineHeight={'16px'} fontWeight={700} color={'#3D0E6B'}>
                          {capitalizeFirstLetter(medicine.Label || '')}
                        </Text>
                        <Text fontSize={'14px'} lineHeight={'16px'} fontWeight={400} color={'#3D0E6B'}>
                          {medicine.Posologia}
                        </Text>
                        {/* <Text fontSize={'12px'} lineHeight={'16px'} fontWeight={400} color={'#3D0E6B'}>
                          {medicine.Quantidade}
                        </Text> */}
                      </Flex>
                      {/* <Flex
                        padding={'2px'}
                        borderRadius={'18px'}
                        bg={'#f2eefb'}
                        alignItems={'center'}
                        justifyContent={'center'}
                        gap={'10px'}
                        whiteSpace={'nowrap'}
                        height={'29px'}
                        width={'40%'}
                      >
                        <Text fontSize={'11px'} fontWeight={600} color={'#3d0e6b'}>
                          Adicionar item
                        </Text>
                        <Checkbox
                          isChecked={
                            medicine.isIncluddedInPharmaModels !== undefined ? medicine.isIncluddedInPharmaModels : true
                          }
                          onChange={() => {
                            onUpdateMedicineCheckbox(
                              medicine.IdMedicamento,
                              medicine.isIncluddedInPharmaModels !== undefined
                                ? !medicine.isIncluddedInPharmaModels
                                : true
                            );
                          }}
                        />
                      </Flex> */}
                    </Flex>
                  </Flex>
                  {index + 1 < currentModelDetail?.Medicamento?.length && <Divider />}
                </>
              ))}
          </Flex>
          {currentModelDetail.Comentario && (
            <Flex flexDirection={'column'} gap={'10px'}>
              <Text
                fontSize={'16px'}
                fontWeight={700}
                lineHeight={'33px'}
                color={'#3D0E6B'}
                textTransform={'capitalize'}
              >
                Observação
              </Text>
              <Flex flexDirection={'row'} alignItems={'center'} gap={'10px'} marginLeft={'10px'}>
                <TextObservation />
                <Text fontSize={'14px'}>{currentModelDetail.Comentario}</Text>
              </Flex>
            </Flex>
          )}
          <Text fontSize={'12px'} mt={'20px'}>
            Modelo de acordo com a bula do(s) medicamento(s)
          </Text>
          <Flex gap={'5px'} flexDirection={{ base: 'column-reverse', sm: 'row' }} justifyContent={'space-between'} mt={'10px'}>
            <Button
              color={'#3d0e6b'}
              border={'1px solid #3d0e6b'}
              variant={'outline'}
              borderRadius={'40px'}
              onClick={() => onBackToModels()}
              isLoading={loading}
              width={{ base: '100%', sm: '50%' }}
              whiteSpace="normal"
            >
              Cancelar
            </Button>
            <Button
              bg={'#3d0e6b'}
              color={'#f8f9fa'}
              borderRadius={'40px'}
              onClick={() => onAddMedicines()}
              isLoading={loading}
              width={{ base: '100%', sm: '50%' }}
              whiteSpace="normal"
            >
              Adicionar Modelo
            </Button>
          </Flex>
        </Box>
      </GrowMotionBox>
    );
  }, [currentModelDetail, onAddMedicines, loading, onDismiss]);

  const calculateTotalItens = (model: ModelDetails) => {
    const items = model?.Medicamento?.length > 1 ? ' itens' : ' item';
    return ((model?.Medicamento?.length || 0) + (model?.MedicamentoManual?.length || 0)).toString() + items;
  };

  const formatMedicineNamesToShow = (model: ModelDetails) => {
    if (!model.Medicamento || model.Medicamento.length === 0) return '';
    return model.Medicamento.map((medicine) => medicine.Nome).join(', ');
  };

  const onRememberLater = useCallback(async () => {
    const [showedCounter] = (await props.handleSponsoredModelsConfig('update', true, props.sponsoredCounter)) as {
      chave: string;
      valor: number;
    }[];

    if (showedCounter.valor === 2) {
      props.handleHighlightedModelEvents('backoffice_prescription_models_view_nevermind');
    } else {
      props.handleHighlightedModelEvents('backoffice_prescription_models_view_remindlater');
    }

    amplitudeModule.popUpClicked({
      pop_up_id: 1,
      pop_up_type: 'modelos patrocinados',
      pop_up_title: 'Modelos',
      pop_up_content: 'Facilite o preenchimento da receita com modelos.',
      pop_up_dismiss: false,
      pop_up_later: true,
      pop_up_agreed: false,
      partner_id: prescriberConfiguration?.idParceiro ?? 0,
      partner_name: prescriberConfiguration?.Nome ?? '',
    });

    props.onClose();
  }, [props, prescriberConfiguration]);

  const ModelDetailsList = useMemo(() => {
    return (
      <Flex direction="column" flex={1} overflow="hidden">
        <Box p={'20px'} pb={'10px'} flexShrink={0}>
          <Flex alignItems={'flex-start'} justifyContent={'space-between'}>
            <Flex flexDir={'column'} gap={'10px'}>
              <Flex alignItems={'center'} gap={'10px'}>
                <HighlightedIcon />
                <Text fontSize={{ base: '18px', md: '20px' }} fontWeight={700} color={'#3D0E6B'}>
                  Modelos
                </Text>
              </Flex>
              <Text fontSize={{ base: '16px', md: '20px' }} fontWeight={700} color={'#2f2f2f'}>
                Facilite o preenchimento da receita com modelos.
              </Text>
              <Text fontSize={{ base: '14px', md: '16px' }} fontWeight={400} color={'#2f2f2f'}>
                Escolha um modelo e finalize sua receita em poucos cliques.
              </Text>
              <Text fontSize={{ base: '14px', md: '16px' }} fontWeight={700} color={'#3D0E6B'}>
                Destaques
              </Text>
            </Flex>

            {/* <IconButton
              size={'xs'}
              aria-label={'close-dialog-model'}
              variant={'ghost'}
              onClick={() => {
                props.handleHighlightedModelEvents('backoffice_prescription_models_view_saiu');
                props.onClose();
              }}
            >
              <CloseIcon />
            </IconButton> */}
          </Flex>
        </Box>
        <Box flex={1} overflowY="auto" px={'20px'} pb={'10px'} css={{ WebkitOverflowScrolling: 'touch' }}>
          <SimpleGrid columns={{ base: 1, sm: 2 }} gap={'10px'} mt={'5px'}>
            {loading ? (
              <Flex justifyContent={'center'} alignItems={'center'} p={'20px'}>
                <Spinner size={'xl'} color="#3D0E6B" />
              </Flex>
            ) : (
              sponsoredModelDetails.map((model) => (
                <Flex>
                  <Box
                    key={model.id}
                    padding={4}
                    border={'1.5px solid #e4e4e4'}
                    borderRadius={'12px'}
                    width={'100%'}
                    _hover={{ border: '1.5px solid #3D0E6B', cursor: 'pointer' }}
                    onClick={() => handleModelDetails(model)}
                  >
                    <Flex flexDirection={'column'} gap={'10px'}>
                      <Flex alignItems={'center'} justifyContent={'space-between'} gap={'10px'} minWidth={0}>
                        <Text fontSize={{ base: '12px', md: '14px' }} fontWeight={600}>
                           {model.Nome}
                        </Text>
                        <Flex alignItems={'center'} gap={'5px'} whiteSpace={'nowrap'} flexShrink={0}>
                          <PillIcon />
                          <Text color={'#3D0E6B'} fontSize={{ base: '11px', md: '12px' }}>{calculateTotalItens(model)}</Text>
                        </Flex>
                      </Flex>

                      <hr />
                      <Text fontSize={{ base: '11px', md: '14px' }} color={'#808080'} noOfLines={2}>
                        {formatMedicineNamesToShow(model)}
                      </Text>
                    </Flex>
                  </Box>
                </Flex>
              ))
            )}
          </SimpleGrid>
        </Box>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          mt={'10px'}
          borderTop={'1px solid #e4e4e4'}
          p={'15px'}
          width={'100%'}
          flexShrink={0}
        >
          <Text
            fontSize={'12px'}
            fontWeight={700}
            color="#3D0E6B"
            _hover={{ cursor: 'pointer', textDecoration: 'underline' }}
            onClick={onRememberLater}
          >
            {props.sponsoredCounter === 1 ? message : 'Lembrar depois'}
          </Text>
        </Box>
      </Flex>
    );
  }, [sponsoredModelDetails, loading, handleModelDetails, props, onRememberLater, message]);

  return (
    <Box 
      bg={'#fff'} 
      borderRadius={'33px'} 
      maxWidth={modalMaxWidth}
      maxHeight={modalMaxHeight}
      overflow="hidden"
      display="flex"
      flexDirection="column"
    >
      <AnimatePresence mode="wait">
        {currentModelDetail ? (
          <Box key={'details'} flex={1} overflowY="auto" css={{ WebkitOverflowScrolling: 'touch' }}>{ModelDetailContent}</Box>
        ) : (
          <Box key={'list'} display="flex" flexDirection="column" flex={1} overflow="hidden">
            {ModelDetailsList}
          </Box>
        )}
      </AnimatePresence>
    </Box>
  );
}
